import { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  File,
  FileSpreadsheet,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { ModelSelector, type ModelId } from "./ModelSelector";

interface FileWithPreview {
  file: File;
  id: string;
  status: "pending" | "uploading" | "processing" | "completed" | "failed";
  progress: number;
  classification?: string;
  error?: string;
}

export interface ExistingDocument {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  status: "pending" | "processing" | "completed" | "failed";
  classification?: string;
  error?: string;
}

interface FileDropZoneProps {
  onFilesSelected: (files: File[], modelId?: ModelId) => void;
  isProcessing?: boolean;
  acceptedFileTypes?: string[];
  maxFiles?: number;
  className?: string;
  existingDocuments?: ExistingDocument[];
  /** Show model selector before processing */
  showModelSelector?: boolean;
  /** Default model to use */
  defaultModel?: ModelId;
}

const FILE_ICONS: Record<string, React.ReactNode> = {
  "application/pdf": <FileText className="h-8 w-8 text-destructive" />,
  "text/csv": <FileSpreadsheet className="h-8 w-8 text-[var(--success)]" />,
  "text/plain": <FileText className="h-8 w-8 text-muted-foreground" />,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": (
    <FileSpreadsheet className="h-8 w-8 text-[var(--success)]" />
  ),
  "application/vnd.ms-excel": (
    <FileSpreadsheet className="h-8 w-8 text-[var(--success)]" />
  ),
  "image/png": <File className="h-8 w-8 text-[var(--info)]" />,
  "image/jpeg": <File className="h-8 w-8 text-[var(--info)]" />,
  "image/jpg": <File className="h-8 w-8 text-[var(--info)]" />,
  default: <File className="h-8 w-8 text-muted-foreground" />,
};

export function FileDropZone({
  onFilesSelected,
  isProcessing = false,
  acceptedFileTypes, // Accept all file types by default - we'll figure out what to do with them
  maxFiles = 20,
  className,
  existingDocuments = [],
  showModelSelector = true,
  defaultModel = "gemini-3-flash-preview",
}: FileDropZoneProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelId>(defaultModel);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Simulate progress animation when processing
  useEffect(() => {
    if (isProcessing) {
      // Clear any existing interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      // Animate progress from current value towards 90% (never complete, parent handles final state)
      progressIntervalRef.current = setInterval(() => {
        setFiles(prev =>
          prev.map(f => {
            if (f.status === "uploading" || f.status === "processing") {
              // Gradually increase progress, slowing down as it approaches 90%
              const remaining = 90 - f.progress;
              const increment = Math.max(1, remaining * 0.1);
              const newProgress = Math.min(90, f.progress + increment);
              return { ...f, progress: newProgress };
            }
            return f;
          })
        );
      }, 200);
    } else {
      // Clear interval when not processing
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isProcessing]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: "pending" as const,
      progress: 0,
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // If no acceptedFileTypes specified, accept all files
    accept: acceptedFileTypes
      ? acceptedFileTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {})
      : undefined,
    maxFiles,
    disabled: isProcessing,
  });

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleProcessFiles = () => {
    if (files.length === 0) return;

    // Update all files to uploading status with initial progress of 25%
    // This represents the "reading files" phase
    setFiles(prev =>
      prev.map(f => ({
        ...f,
        status: "uploading" as const,
        progress: 25,
      }))
    );

    // Pass files and selected model to parent handler
    onFilesSelected(
      files.map(f => f.file),
      selectedModel
    );
  };

  const getFileIcon = (mimeType: string) => {
    return FILE_ICONS[mimeType] || FILE_ICONS.default;
  };

  const getStatusIcon = (status: FileWithPreview["status"]) => {
    switch (status) {
      case "uploading":
      case "processing":
        return <Loader2 className="h-4 w-4 animate-spin text-[var(--info)]" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-[var(--success)]" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  const pendingFiles = files.filter(f => f.status === "pending");
  const processingFiles = files.filter(
    f => f.status === "uploading" || f.status === "processing"
  );
  const hasExistingDocs = existingDocuments.length > 0;
  const completedExisting = existingDocuments.filter(
    d => d.status === "completed"
  );
  const failedExisting = existingDocuments.filter(d => d.status === "failed");

  return (
    <div className={cn("space-y-4", className)}>
      {/* Existing Documents */}
      {hasExistingDocs && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Previously Processed ({completedExisting.length} completed
              {failedExisting.length > 0 && `, ${failedExisting.length} failed`}
              )
            </span>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-2 pr-2">
            {existingDocuments.map(doc => (
              <div
                key={doc.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border bg-card/50",
                  doc.status === "failed" &&
                    "border-destructive/50 bg-destructive/5",
                  doc.status === "completed" &&
                    "border-[var(--success)]/30 bg-[var(--success)]/5"
                )}
              >
                {getFileIcon(doc.mimeType)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{doc.name}</span>
                    {doc.status === "completed" && (
                      <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                    )}
                    {doc.status === "failed" && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{(doc.size / 1024).toFixed(1)} KB</span>
                    {doc.classification && (
                      <>
                        <span>-</span>
                        <span className="capitalize">
                          {doc.classification.replace(/_/g, " ")}
                        </span>
                      </>
                    )}
                    {doc.error && (
                      <span className="text-destructive">{doc.error}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Add more files below to include additional documents
          </p>
        </div>
      )}

      {/* Drop Zone */}
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50",
          isProcessing && "opacity-50 cursor-not-allowed"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-3">
          <Upload
            className={cn(
              "h-12 w-12",
              isDragActive ? "text-primary" : "text-muted-foreground"
            )}
          />
          <div>
            <p className="text-lg font-medium">
              {isDragActive
                ? "Drop files here"
                : "Drag & drop files here, or click to browse"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Drop any document - we'll figure out what to do with it (up to{" "}
              {maxFiles} files)
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              <FileSpreadsheet className="h-3 w-3" /> Bank Statements
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              <FileText className="h-3 w-3" /> Invoices
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              <FileText className="h-3 w-3" /> Contracts
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              <File className="h-3 w-3" /> Any Document
            </span>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {files.length} file{files.length !== 1 ? "s" : ""} selected
            </span>
            {pendingFiles.length > 0 && !isProcessing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFiles([])}
                className="text-destructive hover:text-destructive"
              >
                Clear all
              </Button>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
            {files.map(fileItem => (
              <div
                key={fileItem.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border bg-card",
                  fileItem.status === "failed" &&
                    "border-destructive/50 bg-destructive/5"
                )}
              >
                {getFileIcon(fileItem.file.type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {fileItem.file.name}
                    </span>
                    {getStatusIcon(fileItem.status)}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{(fileItem.file.size / 1024).toFixed(1)} KB</span>
                    {fileItem.classification && (
                      <>
                        <span>-</span>
                        <span className="capitalize">
                          {fileItem.classification.replace(/_/g, " ")}
                        </span>
                      </>
                    )}
                    {fileItem.error && (
                      <span className="text-destructive">{fileItem.error}</span>
                    )}
                  </div>
                  {(fileItem.status === "uploading" ||
                    fileItem.status === "processing") && (
                    <Progress
                      value={fileItem.progress}
                      className="h-1 mt-2 transition-all duration-300"
                    />
                  )}
                </div>
                {fileItem.status === "pending" && !isProcessing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => removeFile(fileItem.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model Selector & Process Button */}
      {pendingFiles.length > 0 && (
        <div className="space-y-3">
          {showModelSelector && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                AI Model for Extraction
              </label>
              <ModelSelector
                value={selectedModel}
                onChange={setSelectedModel}
                disabled={isProcessing}
                variant="compact"
                showCost
              />
            </div>
          )}
          <Button
            onClick={handleProcessFiles}
            disabled={isProcessing}
            className="w-full"
            size="lg"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing {processingFiles.length} files...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Process {pendingFiles.length} file
                {pendingFiles.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
